import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Lock, Loader2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModal';
import { SEOHead } from '../components/SEOHead';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const { openLogin } = useLoginModal();
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/pages/public/${slug}`);
      setPage(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(t('customPage.notFound'));
      } else {
        setError(t('customPage.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Parse dynamic parameters for logged-in users
  const parseContent = (content) => {
    if (!user || !content) return content;
    
    let parsed = content;
    parsed = parsed.replace(/{name}/gi, user.name || '');
    parsed = parsed.replace(/{email}/gi, user.email || '');
    parsed = parsed.replace(/{country}/gi, user.country || '');
    parsed = parsed.replace(/{coins}/gi, String(user.coins || 0));
    parsed = parsed.replace(/{tickets}/gi, String(user.tickets || 0));
    
    return parsed;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">404</h1>
          <p className="text-zinc-500 mb-6">{error}</p>
          <Button onClick={() => navigate('/home')} className="bg-amber-600 hover:bg-amber-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('category.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Check if login is required
  if (page?.visibility === 'login_required' && !user) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex flex-col items-center justify-center p-6">
        <SEOHead 
          title={page.seo?.meta_title || page.title}
          description={page.seo?.meta_description || ''}
        />
        <motion.div 
          className="text-center max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 rounded-full bg-yellow-900/30 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-yellow-500" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">{t('customPage.loginRequired')}</h1>
          <p className="text-zinc-500 mb-6">{t('customPage.loginRequiredDesc')}</p>
          <div className="flex gap-3 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/home')}
              className="border-zinc-700 text-zinc-400"
            >
              {t('category.backToHome')}
            </Button>
            <Button 
              onClick={() => openLogin()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {t('auth.login')}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <SEOHead 
        title={page?.seo?.meta_title || page?.title}
        description={page?.seo?.meta_description || ''}
        image={page?.seo?.og_image || ''}
      />
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/home')}
            className="p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <h1 className="text-lg font-semibold text-white truncate">{page?.title}</h1>
        </div>
      </div>
      
      {/* Content */}
      <motion.div 
        className="max-w-4xl mx-auto px-4 py-8 pb-24"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Created Date */}
        {page?.created_at && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm mb-6">
            <Calendar className="w-4 h-4" />
            <span>{t('customPage.publishedOn')} {formatDate(page.created_at)}</span>
          </div>
        )}
        
        {/* Page Content */}
        <article 
          className="prose prose-invert prose-red max-w-none
            prose-headings:text-white prose-headings:font-bold
            prose-p:text-zinc-300 prose-p:leading-relaxed
            prose-a:text-amber-400 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white
            prose-ul:text-zinc-300 prose-ol:text-zinc-300
            prose-li:marker:text-amber-500
            prose-blockquote:border-l-amber-500 prose-blockquote:bg-zinc-900/50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
            prose-code:text-amber-400 prose-code:bg-zinc-900 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
            prose-pre:bg-zinc-900 prose-pre:border prose-pre:border-zinc-800
            prose-img:rounded-xl prose-img:shadow-lg
            prose-hr:border-zinc-800
          "
          dangerouslySetInnerHTML={{ __html: parseContent(page?.content) }}
        />
      </motion.div>
    </div>
  );
};

export default CustomPage;
